import { useCallback, useRef, type ReactElement } from 'react';
import type { UseSquawkStreamResult } from './useSquawkStream';

const STATUS_DOT: Record<string, string> = {
  disconnected: 'bg-white/35',
  connecting: 'bg-accent-yellow animate-pulse',
  live: 'bg-bull-blue',
  muted: 'bg-white/35',
  error: 'bg-bear-red',
};

const STATUS_TEXT: Record<string, string> = {
  disconnected: 'Disconnected',
  connecting: 'Connecting…',
  live: 'Live',
  muted: 'Muted',
  error: 'Error',
};

/**
 * One stable Play/Stop control (never unmount/swap buttons).
 * On mobile, replacing Stop with Play under the same touch often delivers a
 * ghost click that immediately restarts the stream — status flickers back to Live.
 */
export function StreamControls({
  stream,
  compact = false,
}: {
  stream: UseSquawkStreamResult;
  compact?: boolean;
}): ReactElement {
  const {
    label,
    status,
    volume,
    sdkReady,
    isPlaying,
    lastError,
    start,
    stop,
    setVolume,
  } = stream;

  // Ignore start for a short window after stop (ghost-click guard).
  const suppressStartUntilRef = useRef(0);

  const onToggle = useCallback(() => {
    if (isPlaying) {
      suppressStartUntilRef.current = Date.now() + 400;
      stop();
      return;
    }
    if (Date.now() < suppressStartUntilRef.current) return;
    if (!sdkReady) return;
    void start();
  }, [isPlaying, sdkReady, start, stop]);

  const toggleLabel = isPlaying
    ? status === 'connecting'
      ? 'Cancel'
      : 'Stop'
    : status === 'error'
      ? 'Retry'
      : 'Play';

  return (
    <div
      className={
        compact
          ? 'flex items-center gap-2 min-w-0'
          : 'px-4 py-4 border-b border-dark-border bg-dark-surface'
      }
    >
      {!compact && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white tracking-wide">
            {label}
          </span>
          <div className="flex items-center gap-1.5 max-w-[55%] min-w-0">
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[status] ?? STATUS_DOT.disconnected}`}
            />
            <span className="text-xs text-gray-400 truncate">
              {STATUS_TEXT[status] ?? status}
            </span>
          </div>
        </div>
      )}

      <div className={`flex items-center gap-2 ${compact ? 'flex-1 min-w-0' : ''}`}>
        {compact && (
          <span className="text-[11px] font-semibold text-white shrink-0 w-16 truncate">
            {label}
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          disabled={!isPlaying && !sdkReady}
          className={
            isPlaying
              ? 'min-h-[44px] min-w-[64px] px-3 text-sm font-medium rounded-lg bg-dark-surface-light border border-dark-border text-gray-200'
              : 'min-h-[44px] min-w-[64px] px-3 text-sm font-medium rounded-lg bg-bull-blue/20 border border-bull-blue/40 text-bull-blue disabled:opacity-40'
          }
        >
          {toggleLabel}
        </button>

        <input
          type="range"
          min={0}
          max={100}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          className="flex-1 min-w-0 h-2 accent-bull-blue"
          aria-label={`${label} volume`}
        />
        {!compact && (
          <span className="text-xs text-gray-500 w-9 text-right tabular-nums">
            {volume}%
          </span>
        )}
      </div>

      {!compact && status === 'error' && lastError && (
        <p className="mt-2 text-[11px] text-bear-red leading-snug break-words">
          {lastError}
        </p>
      )}
    </div>
  );
}
